import { z } from 'zod';
import { DateOnlySchema } from './common.js';

/**
 * Statement of Applicability (ISO 27001 Annex A style): one editable row per
 * control of a standard version, recorded versions (snapshots), the editable
 * header text and the change log between versions. Tables soa_controls,
 * soa_versions and soa_settings (migration 0015); routes in
 * apps/worker/src/routes/soa.ts.
 */

export const SOA_REASONS = [
  'Business Requirement',
  'Contractual Requirement',
  'Legal Requirement',
  'Risk Management Requirement',
] as const;
export const SoaReasonSchema = z.enum(SOA_REASONS);
export type SoaReason = z.infer<typeof SoaReasonSchema>;

export const SOA_STATUSES = [
  'implemented',
  'partially_implemented',
  'planned',
  'not_applicable',
] as const;
export const SoaStatusSchema = z.enum(SOA_STATUSES);
export type SoaStatus = z.infer<typeof SoaStatusSchema>;
export const SOA_STATUS_LABELS: Record<SoaStatus, string> = {
  implemented: 'Implemented',
  partially_implemented: 'Partially implemented',
  planned: 'Planned',
  not_applicable: 'Not applicable',
};

/** One ticked reason with its own detail line, e.g. "Risk Management Requirement – R34, R37". */
export const SoaReasonEntrySchema = z.object({
  reason: SoaReasonSchema,
  detail: z.string().max(1000),
});
export type SoaReasonEntry = z.infer<typeof SoaReasonEntrySchema>;

/** Reason entries with each reason at most once (400 validation_failed, path 'reasons'). */
export const SoaReasonsSchema = z
  .array(SoaReasonEntrySchema)
  .max(SOA_REASONS.length)
  .refine((entries) => new Set(entries.map((e) => e.reason)).size === entries.length, {
    message: 'Each reason may be selected only once',
  });

/** Best-practice seed text for one control (packages/standards-engine data/soa). */
export const SoaSeedSchema = z.object({
  reasons: z.array(SoaReasonEntrySchema),
  justification: z.string(),
  implementation: z.string(),
  evidenceNote: z.string(),
});
export type SoaSeed = z.infer<typeof SoaSeedSchema>;

export const SoaControlRowSchema = z.object({
  clauseId: z.string(),
  clauseNumber: z.string(),
  title: z.string(),
  description: z.string(),
  sectionNumber: z.string(),
  sectionTitle: z.string(),
  applicable: z.boolean(),
  reasons: z.array(SoaReasonEntrySchema),
  justification: z.string(),
  implementation: z.string(),
  implementationStatus: SoaStatusSchema,
  statusNote: z.string(),
  ownerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),
  evidenceNote: z.string(),
  /** True when the row is not yet stored and shows the seed text. */
  seeded: z.boolean(),
  /**
   * The standard's seed entry for this control, returned for stored rows as
   * well (null when the standard has no seed) — used for "Reset row to best practice".
   */
  seed: SoaSeedSchema.nullable(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type SoaControlRow = z.infer<typeof SoaControlRowSchema>;

export const SoaVersionSchema = z.object({
  id: z.string(),
  label: z.string(),
  reason: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  approvedById: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedOn: z.string().nullable(),
  createdAt: z.string(),
  /** Number of SoaChange entries versus the previous version (0 for the first). */
  changeCount: z.number().int(),
});
export type SoaVersion = z.infer<typeof SoaVersionSchema>;

export const SoaSummarySchema = z.object({
  total: z.number().int(),
  applicable: z.number().int(),
  notApplicable: z.number().int(),
  implemented: z.number().int(),
  partiallyImplemented: z.number().int(),
  planned: z.number().int(),
});
export type SoaSummary = z.infer<typeof SoaSummarySchema>;

export const SoaSchema = z.object({
  standardVersionId: z.string(),
  standardName: z.string(),
  versionLabel: z.string(),
  orgId: z.string(),
  /** The organisation's own purpose paragraph (soa_settings). */
  purpose: z.string(),
  classification: z.string(),
  controls: z.array(SoaControlRowSchema),
  summary: SoaSummarySchema,
  latestVersion: SoaVersionSchema.nullable(),
});
export type Soa = z.infer<typeof SoaSchema>;

const SOA_TEXT_MAX = 5000;

export const UpdateSoaControlInputSchema = z.object({
  applicable: z.boolean().optional(),
  reasons: SoaReasonsSchema.optional(),
  justification: z.string().max(SOA_TEXT_MAX).optional(),
  implementation: z.string().max(SOA_TEXT_MAX).optional(),
  implementationStatus: SoaStatusSchema.optional(),
  statusNote: z.string().max(SOA_TEXT_MAX).optional(),
  /** null clears the owner; otherwise must be a current org member. */
  ownerUserId: z.string().nullable().optional(),
  evidenceNote: z.string().max(SOA_TEXT_MAX).optional(),
});
export type UpdateSoaControlInput = z.infer<typeof UpdateSoaControlInputSchema>;

export const BulkUpdateSoaInputSchema = z.object({
  controls: z
    .array(UpdateSoaControlInputSchema.extend({ clauseId: z.string().min(1) }))
    .min(1)
    .max(200),
});
export type BulkUpdateSoaInput = z.infer<typeof BulkUpdateSoaInputSchema>;

export const UpdateSoaSettingsInputSchema = z.object({
  purpose: z.string().max(2000),
  classification: z.string().max(100),
});
export type UpdateSoaSettingsInput = z.infer<typeof UpdateSoaSettingsInputSchema>;

export const CreateSoaVersionInputSchema = z.object({
  /** Unique per org + standard version, e.g. "2.4". */
  label: z.string().min(1).max(20),
  reason: z.string().max(1000).default(''),
  /** Must be an org member when set. */
  approvedById: z.string().nullable().optional(),
  approvedOn: DateOnlySchema.optional(),
});
export type CreateSoaVersionInput = z.infer<typeof CreateSoaVersionInputSchema>;

export const SOA_CHANGE_FIELD_KEYS = [
  'applicable',
  'reasons',
  'justification',
  'implementation',
  'implementationStatus',
  'ownerName',
  'evidenceNote',
] as const;
export const SoaChangeFieldSchema = z.enum(SOA_CHANGE_FIELD_KEYS);
export type SoaChangeField = z.infer<typeof SoaChangeFieldSchema>;

/** SoA sheet column heading for each compared field (the change log's "Column"). */
export const SOA_CHANGE_FIELDS: Record<SoaChangeField, string> = {
  applicable: 'Applicable',
  reasons: 'Reason(s) for Selection',
  justification: 'Justification',
  implementation: 'Implementation Details',
  implementationStatus: 'Implementation status',
  ownerName: 'Control owner',
  evidenceNote: 'Evidence',
};

/**
 * One changed cell between two SOA states. `previous`/`next` are the COMPOSED
 * cell texts as the SoA sheet shows them, so a change to a reason detail or a
 * status note is a change to that column.
 */
export const SoaChangeSchema = z.object({
  clauseNumber: z.string(),
  field: SoaChangeFieldSchema,
  fieldLabel: z.string(),
  previous: z.string(),
  next: z.string(),
});
export type SoaChange = z.infer<typeof SoaChangeSchema>;

export const SoaExportQuerySchema = z.object({
  assessmentId: z.string().optional(),
});
export type SoaExportQuery = z.infer<typeof SoaExportQuerySchema>;
